import React, { useMemo } from "react";
import { Text } from "react-native-paper";
import { useTranslation } from "react-i18next";

interface CalculationField {
  name: string;
  label: string;
  calculation: {
    formula: string;
    dependencies: string[];
  };
}

interface CalculationProps {
  field: CalculationField;
  value: string | number;
  onChange: (fieldName: string, value: string) => void;
  formData: Record<string, any>;
}

function convertToArray(depValue: string | string[]) {
  if (Array.isArray(depValue)) {
    return depValue;
  } else if (depValue === "") {
    return [];
  } else {
    return [depValue];
  }
}

export const Calculation: React.FC<CalculationProps> = ({
  field,
  value,
  onChange,
  formData,
}) => {
  const { formula, dependencies } = field.calculation;
  const { t } = useTranslation();

  const calculationTrigger = useMemo(() => {
    return JSON.stringify(dependencies.map((dep: string) => formData[dep]));
  }, [dependencies, formData]);

  React.useEffect(() => {
    try {
      // Replace variables in formula with their values
      let calculatedValue = formula;
      dependencies.forEach((dep: string) => {
        const depValue = formData[dep];
        if (depValue === undefined || depValue === null) {
          calculatedValue = calculatedValue.replace(new RegExp(dep, "g"), "0");
          return;
        }

        let replaceByThisValue = convertToArray(depValue);

        calculatedValue = calculatedValue.replace(
          new RegExp(dep, "g"),
          JSON.stringify(replaceByThisValue),
        );
      });

      // Evaluate the formula
      const result = eval(calculatedValue);
      const numericResult = Number(result);

      if (isNaN(numericResult)) {
        console.error("Calculation resulted in NaN:", {
          calculatedValue,
          result,
        });
        onChange(field.name, "NaN");
        return;
      }

      onChange(field.name, numericResult.toString());
    } catch (error) {
      console.error("Calculation error:", error, {
        formula,
        dependencies,
        formData,
      });
      onChange(field.name, "0");
    }
  }, [calculationTrigger]);

  return (
    <Text variant="titleMedium" style={{ textAlign: "right" }}>
      {t(field.label)}: {!isNaN(Number(value)) && value}
    </Text>
  );
};
